import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { listStateKey } from '../utils/storageKeys';

/**
 * Persists and restores list UI state (search text, page, filters) per route.
 * Uses sessionStorage — state clears when the browser tab is closed.
 *
 * @param {object} initialState  - Default values, e.g. { search: '', page: 1 }
 * @returns {[state, setState, clearState]}
 *
 * Usage:
 *   const [listState, setListState] = useListState({ search: '', page: 1 });
 *   // Read:  listState.search
 *   // Write: setListState(s => ({ ...s, search: e.target.value }))
 */
export const useListState = (initialState = {}) => {
  const { pathname } = useLocation();
  const key = listStateKey(pathname);

  const [state, setState] = useState(() => {
    try {
      const raw = sessionStorage.getItem(key);
      if (raw) return { ...initialState, ...JSON.parse(raw) };
    } catch {}
    return initialState;
  });

  // Write to sessionStorage whenever state changes
  useEffect(() => {
    try {
      sessionStorage.setItem(key, JSON.stringify(state));
    } catch {}
  }, [state, key]);

  const clearState = () => {
    try { sessionStorage.removeItem(key); } catch {}
    setState(initialState);
  };

  return [state, setState, clearState];
};
